import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { signOut } from '@/frontend/lib/auth-client'

export type Role = 'SUPER_ADMIN' | 'MANAGER' | 'STAFF' | 'VIEWER' | 'CUSTOMER' | 'PENDING'

export interface User {
  id: string
  name: string
  email: string
  role: Role
  blocked: boolean
  isDemo: boolean
}

export function getDefaultRoute(role: Role): string {
  switch (role) {
    case 'SUPER_ADMIN':
      return '/dev'
    case 'MANAGER':
    case 'STAFF':
    case 'VIEWER':
      return '/dashboard'
    case 'CUSTOMER':
      return '/profile'
    case 'PENDING':
      return '/pending'
    default:
      return '/profile'
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

export function useSession() {
  return useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => apiFetch<{ user: User | null }>('/api/auth/session'),
    retry: false,
    staleTime: 30_000,
  })
}

export function useCanWrite(): boolean {
  const { data } = useSession()
  const role = data?.user?.role
  return role === 'SUPER_ADMIN' || role === 'MANAGER' || role === 'STAFF'
}

export function useCanDelete(): boolean {
  const { data } = useSession()
  const role = data?.user?.role
  return role === 'SUPER_ADMIN' || role === 'MANAGER'
}

export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: async () => {
      await signOut()
      return { ok: true as const }
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'session'], { user: null })
      queryClient.invalidateQueries({ queryKey: ['auth'] })
      navigate({ to: '/login' })
    },
  })
}
